import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { ArrowLeft, X, Ticket } from 'lucide-react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import LoginModal from '../components/LoginModal';
import { getAssetUrl } from '../utils/asset';

const SHAPE_SELECTOR = 'rect, path, polygon, circle, ellipse';

export default function SeatAllocation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [svgContent, setSvgContent] = useState('');
  const [seatAllocations, setSeatAllocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBooking, setIsBooking] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [requiredSeats, setRequiredSeats] = useState(null);
  const [showSeatPicker, setShowSeatPicker] = useState(true);
  const [transformState, setTransformState] = useState({ scale: 1, positionX: 0, positionY: 0 });
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);
  const fitScaleRef = useRef(null);
  const fitOffsetRef = useRef({ x: 0, y: 0 });

  const selectedSeatsRef = useRef(selectedSeats);
  useEffect(() => {
    selectedSeatsRef.current = selectedSeats;
  }, [selectedSeats]);

  const seatMap = useMemo(() => {
    const map = new Map();
    seatAllocations.forEach(section => {
      const sLabel = section.sectionLabel || section.section || '';
      (section.seats || []).forEach(seat => {
        const item = { ...seat, sectionLabel: sLabel };
        if (seat.seatId) map.set(String(seat.seatId), item);
        if (sLabel && seat.label) {
          map.set(`${sLabel}-${seat.label}`, item);
          map.set(`${sLabel}_${seat.label}`, item);
          map.set(`${sLabel}${seat.label}`, item);
        }
        if (seat.label) {
          map.set(String(seat.label), item);
        }
      });
    });
    return map;
  }, [seatAllocations]);

  // blade-style: { rectEl, label, g } array built from SVG in document order
  const allSeatGroupsRef = useRef([]);

  const [showDetails, setShowDetails] = useState(null);

  useEffect(() => {
    const fetchSeatData = async () => {
      try {
        setIsLoading(true);
        const headers = {
          'Client-ID': 'lokanatya_client',
          'Client-Secret': 'super_secret_key_123'
        };

        const [seatRes, showRes] = await Promise.all([
          fetch(`/api/fetch_images/${id}`, { headers }).then(r => r.json()),
          fetch(`/api/get_product_id/${id}`, { headers }).then(r => r.json()).catch(() => null)
        ]);

        const formatImageUrl = (url) => {
          if (!url) return '';
          let cleanPath = url;
          try {
            if (url.startsWith('http://') || url.startsWith('https://')) {
              const parsed = new URL(url);
              if (url.includes('127.0.0.1') || url.includes('localhost') || url.includes(':8000')) {
                cleanPath = parsed.pathname + parsed.search;
              } else {
                return url;
              }
            }
          } catch (e) { }

          return getAssetUrl(cleanPath);
        };

        if (seatRes.seat_allocation) {
          setSeatAllocations(seatRes.seat_allocation);
        }

        if (showRes && showRes.data) {
          setShowDetails(showRes.data);
        }

        if (seatRes.image) {
          const safeSvgUrl = formatImageUrl(seatRes.image);
          const svgRes = await fetch(safeSvgUrl);
          const svgText = await svgRes.text();
          setSvgContent(svgText);
        }
      } catch (error) {
        console.error("Error fetching seat data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSeatData();
  }, [id]);

  const resolveSeatElement = (target, container) => {
    let el = target;
    while (el && el !== container) {
      // data-seat-id is reliably set on both inner and outer g elements
      const seatId = el.getAttribute && el.getAttribute('data-seat-id');
      if (seatId) {
        const seatData = seatMap.get(String(seatId));
        if (seatData) return { el, seatData, domId: seatId };
      }
      el = el.parentElement;
    }
    return null;
  };

  const getShapeFor = (el) => {
    if (!el) return null;
    const tag = el.tagName ? el.tagName.toLowerCase() : '';
    if (['rect', 'path', 'polygon', 'circle', 'ellipse'].includes(tag)) {
      return el;
    }
    return el.querySelector(SHAPE_SELECTOR);
  };

  const handleSeatQuantitySelect = (qty) => {
    setRequiredSeats(qty);
    setShowSeatPicker(false);
    // Keep selectedSeats empty so user picks manually without auto-selection!
    setSelectedSeats([]);
    selectedSeatsRef.current = [];
    setTimeout(() => {
      highlightSelectedSeats();
    }, 30);
  };

  const handleSvgClick = (e) => {
    const container = e.currentTarget;
    const resolved = resolveSeatElement(e.target, container);

    if (!resolved) return;

    const { el, seatData, domId } = resolved;

    if (seatData && Number(seatData.isBooked || 0) === 0) {
      let currentSelected = [...selectedSeatsRef.current];
      const idx = currentSelected.findIndex(s =>
        String(s.seatId) === String(seatData.seatId)
      );

      let shape = getShapeFor(el) || el;

      if (idx === -1) {
        if (requiredSeats && currentSelected.length >= requiredSeats) {
          let removed = currentSelected.shift();
          if (removed && removed.seatId) {
            let escapedRemovedId = CSS.escape ? CSS.escape(String(removed.seatId)) : String(removed.seatId).replace(/(:|\.|\[|\]|,|=|@)/g, "\\$1");
            let oldEl = container.querySelector('[id="' + escapedRemovedId + '"] rect, rect[id="' + escapedRemovedId + '"]');
            if (oldEl) paintRect(oldEl, '#22c55e');
          }
        }
        currentSelected.push({
          seatId: seatData.seatId,
          label: seatData.label,
          price: Number(seatData.price || 0),
          section: seatData.sectionLabel || seatData.section || '',
          domId: domId
        });
        if (shape) paintRect(shape, '#2563eb');
      } else {
        currentSelected.splice(idx, 1);
        if (shape) paintRect(shape, '#22c55e');
      }

      setSelectedSeats(currentSelected);
      selectedSeatsRef.current = currentSelected;
      setIsPanelExpanded(true);
    }
  };

  const removeSeat = (seatId) => {
    const updated = selectedSeatsRef.current.filter(s => String(s.seatId) !== String(seatId));
    setSelectedSeats(updated);
    selectedSeatsRef.current = updated;

    const container = document.querySelector('.svg-map-container');
    if (container) {
      let escapedId = CSS.escape ? CSS.escape(String(seatId)) : String(seatId).replace(/(:|\.|\[|\]|,|=|@)/g, "\\$1");
      let shape = container.querySelector('[id="' + escapedId + '"] rect, rect[id="' + escapedId + '"]');
      if (shape) paintRect(shape, '#22c55e');
    }
  };

  const removeAllSeats = () => {
    const container = document.querySelector('.svg-map-container');
    if (container) {
      selectedSeatsRef.current.forEach(s => {
        let escapedId = CSS.escape ? CSS.escape(String(s.seatId)) : String(s.seatId).replace(/(:|\.|\[|\]|,|=|@)/g, "\\$1");
        let shape = container.querySelector('[id="' + escapedId + '"] rect, rect[id="' + escapedId + '"]');
        if (shape) paintRect(shape, '#22c55e');
      });
    }
    setSelectedSeats([]);
    selectedSeatsRef.current = [];
  };

  // Paint a single rect element directly (same as blade's paintSeat)
  const paintRect = (rectEl, color) => {
    if (!rectEl || !rectEl.style) return;
    rectEl.style.setProperty('fill', color, 'important');
    rectEl.setAttribute('fill', color);
  };

  // Blade-identical: scan all <g> elements with a DIRECT CHILD <rect> in SVG document order
  const buildSeatGroupsFromSvg = (container) => {
    const svg = container.querySelector('svg');
    if (!svg) return [];

    // Make ALL text elements non-clickable (same as blade)
    svg.querySelectorAll('text').forEach(t => { t.style.pointerEvents = 'none'; });

    const groups = [];
    svg.querySelectorAll('g').forEach(g => {
      // Only g elements whose DIRECT child is a rect
      let rect = null;
      for (const child of g.children) {
        if (child.tagName && child.tagName.toLowerCase() === 'rect') {
          rect = child;
          break;
        }
      }
      if (!rect) return;
      // Skip bounding box rects (fill-opacity:0, hidden, or oversized)
      const style = rect.getAttribute('style') || '';
      if (style.indexOf('fill-opacity: 0') !== -1) return;
      if (style.indexOf('visibility: hidden') !== -1) return;
      const w = parseFloat(rect.getAttribute('width') || '0');
      const h = parseFloat(rect.getAttribute('height') || '0');
      if (w > 60 || h > 60) return;
      // Find text label in this g or next sibling g
      let textEl = g.querySelector('text');
      if (!textEl && g.nextElementSibling) {
        textEl = g.nextElementSibling.querySelector('text');
      }
      if (textEl) {
        const txt = textEl.textContent.replace(/\s+/g, '');
        if (txt.length > 0) {
          // Find the closest ancestor <g> that has an id (the outer UUID_LABEL group)
          let outerG = g.parentElement;
          while (outerG && outerG !== svg) {
            if (outerG.id) break;
            outerG = outerG.parentElement;
          }
          groups.push({ rectEl: rect, label: txt, g, outerG: outerG || g });
        }
      }
    });
    return groups;
  };

  // Blade-identical highlight: direct ID matching, auto-healing rects, clean section headers
  const highlightSelectedSeats = () => {
    const container = document.querySelector('.svg-map-container');
    if (!container || seatAllocations.length === 0) return;

    const svg = container.querySelector('svg');
    if (!svg) return;

    svg.querySelectorAll('text').forEach(t => { t.style.pointerEvents = 'none'; });

    const selectedIds = new Set(selectedSeatsRef.current.map(s => String(s.seatId)));
    const validSeatIds = new Set();

    // 1. Direct ID matching + auto-healing
    seatAllocations.forEach(section => {
      (section.seats || []).forEach(seatInfo => {
        if (!seatInfo.seatId) return;
        validSeatIds.add(String(seatInfo.seatId));

        let escapedId = CSS.escape ? CSS.escape(String(seatInfo.seatId)) : String(seatInfo.seatId).replace(/(:|\.|\[|\]|,|=|@)/g, "\\$1");
        let seatG = svg.querySelector('[id="' + escapedId + '"]');
        let rectEl = null;

        if (seatG) {
          if (['rect', 'path', 'polygon', 'circle'].includes(seatG.tagName.toLowerCase())) {
            rectEl = seatG;
          } else {
            rectEl = seatG.querySelector('rect, path, polygon, circle');
            if (!rectEl) {
              // Auto-heal: group exists but inner seat shape was missing
              rectEl = document.createElementNS("http://www.w3.org/2000/svg", "rect");
              rectEl.setAttribute("data-auto-healed", "true");
              rectEl.setAttribute("data-seat-id", String(seatInfo.seatId));
              rectEl.setAttribute("x", "-14");
              rectEl.setAttribute("y", "-14");
              rectEl.setAttribute("width", "28");
              rectEl.setAttribute("height", "28");
              rectEl.setAttribute("rx", "4");
              rectEl.setAttribute("ry", "4");
              rectEl.setAttribute("style", "stroke: rgba(0,0,0,0.2); stroke-width: 1px;");
              seatG.appendChild(rectEl);
            }
          }

          // Ensure text number label exists inside seat group
          let textNode = seatG.querySelector('text');
          if (!textNode) {
            textNode = document.createElementNS("http://www.w3.org/2000/svg", "text");
            textNode.setAttribute("font-size", "9");
            textNode.setAttribute("font-weight", "700");
            textNode.setAttribute("fill", "#1e293b");
            textNode.setAttribute("text-anchor", "middle");
            textNode.setAttribute("dominant-baseline", "central");
            textNode.setAttribute("pointer-events", "none");
            seatG.appendChild(textNode);
          }
          if (!textNode.textContent || textNode.textContent.trim() === '') {
            textNode.textContent = seatInfo.label;
          }
          textNode.style.pointerEvents = "none";
        } else {
          rectEl = svg.querySelector('rect[id="' + escapedId + '"], path[id="' + escapedId + '"]');
        }

        if (!rectEl) return;

        rectEl.setAttribute('data-seat-id', seatInfo.seatId);
        if (seatG) seatG.setAttribute('data-seat-id', seatInfo.seatId);

        const isBooked = Number(seatInfo.isBooked || 0);
        const isSelected = selectedIds.has(String(seatInfo.seatId));

        if (isSelected) {
          paintRect(rectEl, '#2563eb');
          rectEl.style.cursor = 'pointer';
        } else if (isBooked === 1) {
          paintRect(rectEl, '#eab308');
          rectEl.style.cursor = 'not-allowed';
        } else if (isBooked === 2) {
          paintRect(rectEl, '#ef4444');
          rectEl.style.cursor = 'not-allowed';
        } else {
          paintRect(rectEl, '#22c55e');
          rectEl.style.cursor = 'pointer';
        }
      });
    });

    // Remove any stray auto-healed rects that don't correspond to a valid seat
    svg.querySelectorAll('[data-auto-healed="true"]').forEach(el => {
      const sid = el.getAttribute('data-seat-id') || el.parentNode?.id;
      if (!sid || !validSeatIds.has(String(sid))) {
        el.remove();
      }
    });

    // 2. Render Section Title Text directly above each section inside the SVG (Clean previous layout style)
    seatAllocations.forEach(section => {
      if (!section.seats || section.seats.length === 0) return;
      let minX = Infinity;
      let minY = Infinity;
      let sectionFirstG = null;

      section.seats.forEach(sInfo => {
        if (!sInfo.seatId) return;
        let escapedId = CSS.escape ? CSS.escape(String(sInfo.seatId)) : String(sInfo.seatId).replace(/(:|\.|\[|\]|,|=|@)/g, "\\$1");
        let sG = svg.querySelector('[id="' + escapedId + '"]');
        if (sG) {
          if (!sectionFirstG) sectionFirstG = sG;
          let transform = sG.getAttribute('transform') || '';
          let match = transform.match(/matrix\s*\(([^)]+)\)/);
          if (match) {
            let parts = match[1].split(/[\s,]+/).map(parseFloat);
            if (parts.length >= 6) {
              let x = parts[4];
              let y = parts[5];
              if (x < minX) minX = x;
              if (y < minY) minY = y;
            }
          }
        }
      });

      if (sectionFirstG && minX !== Infinity && minY !== Infinity) {
        let sectionIdAttr = String(section.sectionId || section.sectionLabel || section.section || '').replace(/\s+/g, '_');
        let existingHeader = svg.querySelector(`[data-section-header="${sectionIdAttr}"]`);
        if (!existingHeader) {
          let headerG = document.createElementNS("http://www.w3.org/2000/svg", "g");
          headerG.setAttribute('data-section-header', sectionIdAttr);
          headerG.setAttribute('transform', `matrix(1 0 0 1 ${minX} ${minY - 22})`);

          let textNode = document.createElementNS("http://www.w3.org/2000/svg", "text");
          textNode.setAttribute("font-size", "15");
          textNode.setAttribute("font-weight", "bold");
          textNode.setAttribute("fill", "#0f172a");
          textNode.setAttribute("font-family", "Arial, sans-serif");
          textNode.setAttribute("pointer-events", "none");
          textNode.textContent = section.sectionLabel || section.section || 'SECTION';

          headerG.appendChild(textNode);

          if (sectionFirstG.parentNode) {
            sectionFirstG.parentNode.insertBefore(headerG, sectionFirstG);
          } else {
            svg.appendChild(headerG);
          }
        }
      }
    });
  };

  // Rebuild seat groups and highlight whenever SVG or seatAllocations initial fetch completes
  useEffect(() => {
    if (!svgContent || seatAllocations.length === 0) return;
    allSeatGroupsRef.current = [];
    const raf = requestAnimationFrame(() => {
      highlightSelectedSeats();
    });
    return () => cancelAnimationFrame(raf);
  }, [svgContent, seatAllocations, isMapReady]);

  const handleSvgClickRef = useRef();
  useEffect(() => {
    handleSvgClickRef.current = handleSvgClick;
  });

  const [isMinimapVisible, setIsMinimapVisible] = useState(false);
  const [minimapBox, setMinimapBox] = useState({ left: '0%', top: '0%', width: '100%', height: '100%', miniScale: 1, miniPanX: 0, miniPanY: 0 });
  const minimapTimerRef = useRef(null);

  const updateMinimap = (ref) => {
    setIsMinimapVisible(true);

    const state = ref?.state || ref?.transformState || ref?.instance?.transformState;
    if (state) {
      const { scale, positionX, positionY } = state;
      const wrapper = ref.instance?.wrapperComponent || document.querySelector('.react-transform-wrapper');
      const content = ref.instance?.contentComponent || document.querySelector('.react-transform-component');

      const wW = wrapper ? wrapper.clientWidth : window.innerWidth;
      const wH = wrapper ? wrapper.clientHeight : window.innerHeight;
      const cW = content ? content.clientWidth * scale : window.innerWidth * scale;
      const cH = content ? content.clientHeight * scale : window.innerHeight * scale;

      if (cW > 0 && cH > 0) {
        const visibleWidthPct = Math.min(100, Math.max(15, (wW / cW) * 100));
        const visibleHeightPct = Math.min(100, Math.max(15, (wH / cH) * 100));
        const leftPct = Math.min(100 - visibleWidthPct, Math.max(0, (-positionX / cW) * 100));
        const topPct = Math.min(100 - visibleHeightPct, Math.max(0, (-positionY / cH) * 100));

        const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
        const baseScale = isMobile ? 0.45 : 0.6;
        const zoomRatio = scale / baseScale;
        const miniScale = Math.min(3, Math.max(1, zoomRatio));

        const centerX = (leftPct + visibleWidthPct / 2) - 50;
        const centerY = (topPct + visibleHeightPct / 2) - 50;

        const miniPanX = -centerX * 0.5;
        const miniPanY = -centerY * 0.5;

        setMinimapBox({
          left: `${leftPct}%`,
          top: `${topPct}%`,
          width: `${visibleWidthPct}%`,
          height: `${visibleHeightPct}%`,
          miniScale,
          miniPanX,
          miniPanY
        });
      }
    }

    if (minimapTimerRef.current) clearTimeout(minimapTimerRef.current);
    minimapTimerRef.current = setTimeout(() => {
      setIsMinimapVisible(false);
    }, 2500);
  };

  const mapElement = React.useMemo(() => {
    if (isLoading) {
      return (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-600"></div>
        </div>
      );
    }
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const initialX = isMobile ? 2.5 : 386.5;
    const initialY = isMobile ? 32.5 : 3.5;
    const initialScaleVal = isMobile ? 0.45 : 0.6;
    const minScaleVal = isMobile ? 0.45 : 0.6;

    return (
      <TransformWrapper
        initialScale={initialScaleVal}
        initialPositionX={initialX}
        initialPositionY={initialY}
        minScale={minScaleVal}
        maxScale={12}
        centerOnInit={false}
        limitToBounds={false}
        wheel={{ step: 0.03, smoothStep: 0.003 }}
        doubleClick={{ mode: 'zoomIn', step: 0.2 }}
        panning={{ velocityDisabled: true }}
        onInit={(ref) => {
          setTimeout(() => {
            if (ref && ref.setTransform) {
              ref.setTransform(initialX, initialY, initialScaleVal, 200);
            }
            setIsMapReady(true);
            updateMinimap(ref);
          }, 100);
        }}
        onZoom={(ref) => updateMinimap(ref)}
        onPanning={(ref) => updateMinimap(ref)}
        onZoomStart={(ref) => updateMinimap(ref)}
        onPanningStart={(ref) => updateMinimap(ref)}
        onPanningStop={(ref) => {
          updateMinimap(ref);
          if (ref.state) {
            const { positionX, positionY, scale } = ref.state;
            const w = window.innerWidth;
            const h = window.innerHeight;
            
            // If they are near the default zoom out level, use strict snap-back to prevent getting lost
            if (scale <= initialScaleVal + 0.2) {
              if (Math.abs(positionX - initialX) > (w * 0.6) || Math.abs(positionY - initialY) > (h * 0.6)) {
                ref.setTransform(initialX, initialY, initialScaleVal, 500);
              }
            } else {
              // If they are zoomed in, only snap back if they pan ridiculously far into nowhere
              if (Math.abs(positionX) > (w * scale * 2) || Math.abs(positionY) > (h * scale * 2)) {
                ref.setTransform(initialX, initialY, initialScaleVal, 500);
              }
            }
          }
        }}
        onTransformed={(ref) => {
          updateMinimap(ref);
          if (ref.state && ref.state.scale < minScaleVal - 0.005) {
            ref.setTransform(initialX, initialY, initialScaleVal, 200);
          }
        }}
      >
        {({ zoomIn, zoomOut, setTransform }) => (
          <>
            <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
              <button onClick={() => zoomIn()} className="bg-white p-2 rounded-lg shadow-md hover:bg-gray-50 font-bold text-lg w-9 h-9 flex items-center justify-center">+</button>
              <button onClick={() => zoomOut()} className="bg-white p-2 rounded-lg shadow-md hover:bg-gray-50 font-bold text-lg w-9 h-9 flex items-center justify-center">-</button>
              <button onClick={() => setTransform(initialX, initialY, initialScaleVal, 200)} className="bg-white p-2 rounded-lg shadow-md hover:bg-gray-50 text-xs font-bold w-9 h-9 flex items-center justify-center">↺</button>
            </div>
            <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }}>
              <div
                className="svg-map-container"
                dangerouslySetInnerHTML={{ __html: svgContent }}
                onClick={(e) => handleSvgClickRef.current && handleSvgClickRef.current(e)}
                style={{ touchAction: 'manipulation' }}
              />
            </TransformComponent>
          </>
        )}
      </TransformWrapper>
    );
  }, [isLoading, svgContent]);

  const totalPrice = selectedSeats.reduce((acc, curr) => acc + curr.price, 0);

  const proceedToBooking = async (user, token) => {
    const payload = {
      product_id: id,
      seats: selectedSeats.map(s => s.seatId),
      total_amount: totalPrice,
      seat_name: selectedSeats.map(s => `${s.section}-${s.label}`),
      seat_price: selectedSeats.map(s => s.price),
      user_id: user.id
    };

    try {
      const res = await fetch('/api/check_ticket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Client-ID': 'lokanatya_client',
          'Client-Secret': 'super_secret_key_123'
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (data.success) {
        navigate('/cart');
      } else {
        Swal.fire('Error', data.message || 'Error booking seats', 'error');
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Network error while booking seats', 'error');
    }
  };

  const handleBookNow = async () => {
    if (selectedSeats.length === 0) {
      Swal.fire('Warning', 'Please select at least one seat.', 'warning');
      return;
    }

    const token = localStorage.getItem('auth_token');
    const userDataStr = localStorage.getItem('user_data');

    if (token && userDataStr) {
      const user = JSON.parse(userDataStr);
      setIsBooking(true);
      await proceedToBooking(user, token);
      setIsBooking(false);
    } else {
      setIsLoginModalOpen(true);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#f9fafb', overflow: 'hidden', position: 'relative' }}>
      {showSeatPicker && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-4 sm:p-5 text-center">
              <h2 className="text-lg sm:text-xl font-extrabold text-gray-900 mb-4">How many seats?</h2>

              <div className="flex flex-wrap justify-center gap-2.5 mb-5">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                  <button
                    key={num}
                    onClick={() => setRequiredSeats(num)}
                    className={`w-10 h-10 rounded-full text-base font-bold transition-all ${requiredSeats === num
                      ? 'bg-red-500 text-white shadow-md shadow-red-500/40 scale-105'
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-100'
                      }`}
                  >
                    {num}
                  </button>
                ))}
              </div>

              <button
                onClick={() => {
                  if (requiredSeats) handleSeatQuantitySelect(requiredSeats);
                }}
                disabled={!requiredSeats}
                className="w-full py-2.5 bg-red-500 text-white rounded-xl font-bold text-sm shadow-md shadow-red-500/30 disabled:opacity-50 disabled:shadow-none hover:bg-red-600 transition-colors active:scale-95"
              >
                Select Seats
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .svg-map-container svg {
          display: block;
        }
        /* Normalize all default raw SVG available seat shapes to uniform bright green #86efac */
        .svg-map-container rect[fill*="209"],
        .svg-map-container rect[fill*="d1fae5"],
        .svg-map-container rect[fill*="ecfdf5"],
        .svg-map-container rect[fill="rgb(209,250,229)"],
        .svg-map-container path[fill*="209"],
        .svg-map-container path[fill*="d1fae5"],
        .svg-map-container path[fill="rgb(209,250,229)"] {
          fill: #86efac !important;
        }
        .svg-map-container path,
        .svg-map-container rect,
        .svg-map-container polygon,
        .svg-map-container circle,
        .svg-map-container ellipse,
        .svg-map-container g {
          cursor: pointer !important;
        }
        .minimap-svg-container svg {
          width: 100% !important;
          height: 100% !important;
          display: block !important;
        }
      `}</style>

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onSuccess={async (user, token) => {
          setIsBooking(true);
          await proceedToBooking(user, token);
          setIsBooking(false);
        }}
      />

      <header className="bg-white px-4 py-2 flex items-center shadow-xs z-30 relative border-b border-gray-100 min-h-[48px]">
        <button onClick={() => navigate(-1)} className="mr-3 p-1.5 rounded-full bg-gray-50 border border-gray-100 hover:bg-gray-100 text-gray-700 transition active:scale-95">
          <ArrowLeft size={16} strokeWidth={2.5} />
        </button>
        <div>
          <h1 className="text-sm sm:text-base font-bold text-gray-900 tracking-tight leading-tight">
            {showDetails?.jatra_name || showDetails?.product_name || 'Select Your Seats'}
          </h1>
          <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">
            {showDetails?.cat_name ? `${showDetails.cat_name} • Select Seats` : 'Select Your Seats'}
          </p>
        </div>
      </header>

      <div
        style={{ flex: 1, position: 'relative', background: '#fcfcfc', overflow: 'hidden', cursor: 'grab' }}
        onMouseDown={() => setIsMinimapVisible(true)}
        onTouchStart={() => setIsMinimapVisible(true)}
        onTouchMove={() => setIsMinimapVisible(true)}
        onWheel={() => setIsMinimapVisible(true)}
      >
        {/* Floating BookMyShow-style Minimap Overlay in Bottom Right Corner */}
        <div
          className={`absolute bottom-20 right-3 z-30 w-32 h-24 sm:w-40 sm:h-28 bg-white/95 backdrop-blur-md rounded-xl shadow-2xl border border-gray-200 p-1.5 transition-all duration-300 pointer-events-none ${isMinimapVisible && isMapReady ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
            }`}
        >
          <div className="relative w-full h-full rounded-lg overflow-hidden bg-slate-50 flex items-center justify-center border border-gray-100">
            <div
              className="minimap-svg-container w-full h-full flex items-center justify-center pointer-events-none select-none opacity-90 transition-transform duration-100"
              style={{
                transform: `translate(${minimapBox.miniPanX || 0}%, ${minimapBox.miniPanY || 0}%) scale(${minimapBox.miniScale || 1})`,
                transformOrigin: 'center center'
              }}
              dangerouslySetInnerHTML={{ __html: svgContent }}
            />

            {/* Active Viewbox Indicator Box */}
            <div
              className="absolute border-2 border-red-500 bg-red-500/20 rounded-sm transition-all duration-75 shadow-2xs pointer-events-none z-10"
              style={{
                left: minimapBox.left,
                top: minimapBox.top,
                width: minimapBox.width,
                height: minimapBox.height
              }}
            />

            <div className="absolute bottom-1 right-1.5 text-[7px] font-black text-red-600 uppercase tracking-widest bg-white/90 px-1 py-0.5 rounded shadow-2xs">
              VENUE MAP
            </div>
          </div>
        </div>

        {(!isMapReady || isLoading) && (
          <div className="absolute inset-0 z-40 bg-white/95 backdrop-blur-xs flex flex-col items-center justify-center gap-3">
            <div className="w-10 h-10 border-4 border-red-200 border-t-red-600 rounded-full animate-spin"></div>
            <span className="text-[11px] font-extrabold text-gray-500 uppercase tracking-widest animate-pulse">
              Loading Seats...
            </span>
          </div>
        )}

        <div style={{ opacity: isMapReady ? 1 : 0, transition: 'opacity 0.3s ease', width: '100%', height: '100%' }}>
          {mapElement}
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          background: 'white',
          borderRadius: '2.5rem 2.5rem 0 0',
          boxShadow: '0 -12px 40px rgba(0,0,0,0.12)',
          zIndex: 20,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transform: isPanelExpanded ? 'translateY(0)' : 'translateY(calc(100% - 76px))',
          transition: 'transform 0.5s cubic-bezier(0.32,0.72,0,1)'
        }}
      >
        {/* Pull indicator & Legend */}
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', paddingTop: '20px', paddingBottom: '12px', background: 'white', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', position: 'relative', userSelect: 'none' }}
          onClick={() => setIsPanelExpanded(prev => !prev)}
        >
          {/* Handle bar */}
          <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-10 h-1 bg-gray-300 rounded-full"></div>

          <div className="flex items-center gap-1 mt-2">
            <span className="w-2 h-2 rounded-full bg-[#86efac] border border-[#22c55e]"></span>
            <span className="text-[9px] sm:text-[10px] font-extrabold text-gray-500 uppercase tracking-wide">Available</span>
          </div>
          <div className="flex items-center gap-1 mt-2">
            <span className="w-2 h-2 rounded-full bg-[#2563eb]"></span>
            <span className="text-[9px] sm:text-[10px] font-extrabold text-gray-500 uppercase tracking-wide">Selected</span>
          </div>
          <div className="flex items-center gap-1 mt-2">
            <span className="w-2 h-2 rounded-full bg-[#eab308]"></span>
            <span className="text-[9px] sm:text-[10px] font-extrabold text-gray-500 uppercase tracking-wide">Offline</span>
          </div>
          <div className="flex items-center gap-1 mt-2">
            <span className="w-2 h-2 rounded-full bg-[#ef4444]"></span>
            <span className="text-[9px] sm:text-[10px] font-extrabold text-gray-500 uppercase tracking-wide">Booked</span>
          </div>
        </div>

        <div className="px-4 sm:px-6 py-3.5 sm:py-5 min-h-[90px] max-h-[160px] overflow-y-auto bg-white">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Ticket size={16} className="text-blue-600 shrink-0" />
              <h3 className="text-xs font-black text-gray-900 uppercase tracking-widest">Selected Seats</h3>
            </div>
            {selectedSeats.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full">
                  {selectedSeats.length} Seat(s)
                </span>
                <button
                  onClick={removeAllSeats}
                  className="text-[10px] font-extrabold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200/60 px-2 py-0.5 rounded-md transition-colors flex items-center gap-1 active:scale-95 cursor-pointer"
                  title="Cancel All Selected Seats"
                >
                  <X size={12} strokeWidth={2.5} />
                  <span>Cancel All</span>
                </button>
              </div>
            )}
          </div>
          {selectedSeats.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-3">
              <p className="text-xs text-gray-400 font-medium">No seats selected yet.</p>
              <p className="text-[10px] text-gray-300 mt-0.5 uppercase tracking-widest">Tap on the map to choose your seats</p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 max-h-[130px] overflow-y-auto pr-1">
              {selectedSeats.map(seat => (
                <div key={seat.seatId} className="relative flex items-stretch bg-white shadow-2xs rounded-lg overflow-hidden shrink-0 group hover:shadow-xs transition-shadow cursor-default max-w-full">

                  {/* Left part */}
                  <div className="bg-[#f0f5ff] text-blue-700 pl-3 pr-2 py-1 text-[11px] sm:text-[12px] font-extrabold flex items-center justify-center tracking-wide truncate">
                    {seat.section}-{seat.label}
                  </div>

                  {/* Middle Separator */}
                  <div className="border-l-2 border-dashed border-blue-300 opacity-70"></div>

                  {/* Right part */}
                  <div className="flex items-center bg-white pr-1">
                    <div className="px-2 py-1 text-[11px] sm:text-[12px] font-black text-slate-800">
                      ₹{seat.price}
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); removeSeat(seat.seatId); }} className="pr-2 pl-0.5 py-1 text-slate-400 hover:text-red-500 transition-colors focus:outline-none h-full flex items-center justify-center cursor-pointer relative z-30">
                      <X size={13} strokeWidth={2.5} />
                    </button>
                  </div>

                  {/* Outer Border (overlay) */}
                  <div className="absolute inset-0 border border-blue-300 rounded-lg pointer-events-none z-10"></div>

                  {/* Left Cutout */}
                  <div className="absolute top-1/2 -translate-y-1/2 -left-[7px] w-[14px] h-[14px] bg-white border border-blue-300 rounded-full z-20 pointer-events-none"></div>

                  {/* Right Cutout */}
                  <div className="absolute top-1/2 -translate-y-1/2 -right-[7px] w-[14px] h-[14px] bg-white border border-blue-300 rounded-full z-20 pointer-events-none"></div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-5 flex items-center justify-between border-t border-gray-50 bg-white">
          <div>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-0.5">Total Payable</p>
            <p className="text-2xl font-black text-gray-900 leading-none">₹{totalPrice}</p>
          </div>
          <button
            onClick={handleBookNow}
            disabled={selectedSeats.length === 0 || isBooking}
            className={`px-10 py-3.5 rounded-full font-black text-sm text-white transition-all shadow-md flex items-center gap-2 ${selectedSeats.length > 0 && !isBooking
              ? 'bg-slate-900 hover:bg-black hover:shadow-lg hover:shadow-slate-900/20 transform hover:-translate-y-0.5 active:scale-95'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
              }`}
          >
            {isBooking ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                PROCESSING...
              </>
            ) : (
              'BOOK NOW'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
